import { createSlice, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "~/store";
import { createThunk } from "~/thunks/createThunks";
import { fetchSurvey } from "~/thunks/surveyThunks";
import { updateSingleThunk } from "~/thunks/updateThunks";
import type { Log, LogViewType } from "~/types/tasks";


interface LogsState {
  logs: Log[];
  selectedLogs: Log[];
  processLogs: any[];
  showCheckbox: boolean;
  gettingLogs: boolean;
  activeView?: LogViewType;
}

const initialState: LogsState = {
  logs: [],
  selectedLogs: [],
  processLogs: [],
  activeView: "all",
  showCheckbox: false,
  gettingLogs: false,
};

const logsSlice = createSlice({
  name: "logs",
  initialState,
  reducers: {
    setLogs(state, action: PayloadAction<any[]>) {
      state.logs = action.payload;
      state.activeView = "all";
    },
    addLog(state, action: PayloadAction<any>) {
      state.logs.push(action.payload);
    },
    setProcessLogs(state, action: PayloadAction<any[]>) {
      state.processLogs = action.payload;
    },
    setActiveView(state, action: PayloadAction<LogViewType>) {
      state.activeView = action.payload;
    },
    setSelectedLogs(state, action: PayloadAction<any[]>) {
      state.selectedLogs = action.payload;
    },
    setShowCheckbox(state, action: PayloadAction<boolean>) {
      state.showCheckbox = action.payload;
    },
    setGettingLogs(state, action: PayloadAction<boolean>) {
      state.gettingLogs = action.payload;
    },
    resetLogsState(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>)  => {
        const { dataType, data } = action.payload;
        if (dataType === 'logs') {
          state.logs.push(data);
        }
      })
      .addCase(updateSingleThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>)  => {
        const { dataType, data } = action.payload;
        if (dataType === 'logs') {
          const logIndex = state.logs.findIndex(log => log.id === data.id);
          if (logIndex !== -1) {
            state.logs[logIndex] = {
              ...state.logs[logIndex],
              ...data,
            };
          }
        }
      })
      .addCase(fetchSurvey.pending, (state, action) => {
        Object.assign(state, initialState);
      });
  }
});

export const {
  setLogs,
  setSelectedLogs,
  setShowCheckbox,
  setGettingLogs,
  resetLogsState,
  setActiveView,
  addLog,
  setProcessLogs
} = logsSlice.actions;

export default logsSlice.reducer;

export const selectLogsState = (state: RootState) => state.logs;
export const selectLogs = (state: RootState) => state.logs.logs;
export const selectProcessLogs = (state: RootState) => state.logs.processLogs;
export const selectSelectedLogs = (state: RootState) => state.logs.selectedLogs;
export const selectShowCheckbox = (state: RootState) => state.logs.showCheckbox;
export const selectGettingLogs = (state: RootState) => state.logs.gettingLogs;
export const selectActiveView = (state: RootState) => state.logs.activeView;
export const selectHasUploadingLogs = createSelector(
  [selectProcessLogs],
  (processLogs) => processLogs.some((log: any) => log.status?.includes("Uploading"))
);
